import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSend,
  FiSearch,
  FiMoreVertical,
  FiEdit2,
  FiTrash2,
  FiMessageCircle,
  FiUsers,
  FiClock,
  FiAlertCircle,
  FiVolume2,
  FiVolumeX,
  FiX,
} from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import communityService from "../../services/communityService";
import communitySocketService from "../../services/communitySocketService";
import { getChatPermissions } from "../../utils/communityPermissions";
import {
  validateMessageContent,
  sanitizeInput,
  logSecurityEvent,
  rateLimiter,
  handleSecureError,
} from "../../utils/securityUtils";
import "./styles/index.css";

// Time formatting
const formatDistanceToNow = (date) => {
  const now = new Date();
  const diffMs = now - new Date(date);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(date).toLocaleDateString();
};

// Animation variants
const messageVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" },
  },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.15 } },
};

const CommunityChat = ({
  communityId,
  community,
  userRole,
  userMembership,
  isReadOnly,
}) => {
  const { user } = useUserContext();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Map());
  const [onlineMembers, setOnlineMembers] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [notifications, setNotifications] = useState(true);
  const [socketConnected, setSocketConnected] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Refs
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Load initial messages
  useEffect(() => {
    if (communityId && community) {
      loadMessages();
    }
  }, [communityId, community]);

  // Initialize socket connection
  useEffect(() => {
    if (!communityId || !community || !user) return;

    let isEffectActive = true;

    const initializeSocket = async () => {
      try {
        if (!isEffectActive) return;

        if (user) {
          communitySocketService.setUserInfo(
            user.id || user._id,
            user.name || user.username,
            user.profilePic || user.avatar
          );
        }

        await communitySocketService.connect();

        if (!isEffectActive) return;

        communitySocketService.joinCommunity(communityId, (onlineUsers) => {
          if (onlineUsers && onlineUsers.length > 0) {
            setOnlineMembers(onlineUsers);
          }
        });

        communitySocketService.onNewMessage(handleNewMessage);
        communitySocketService.onMessageUpdated(handleMessageUpdated);
        communitySocketService.onMessageDeleted(handleMessageDeleted);
        communitySocketService.onUserJoined(handleUserJoined);
        communitySocketService.onUserLeft(handleUserLeft);
        communitySocketService.onTypingStart(handleTypingStart);
        communitySocketService.onTypingStop(handleTypingStop);

        setSocketConnected(true);
      } catch (error) {
if (isEffectActive) {
          if (error.message && error.message.includes("Invalid namespace")) {
            toast.warn("Real-time chat not available yet");
          } else {
            toast.error("Failed to connect to real-time chat");
          }
        }
      }
    };

    initializeSocket();

    return () => {
      isEffectActive = false;
      if (communityId) {
        communitySocketService.leaveCommunity(communityId);
      }
    };
  }, [communityId, community, user]);

  useEffect(() => {
    return () => {
      communitySocketService.disconnect();
    };
  }, []);

  const chatPermissions = getChatPermissions(community, userMembership);
  const canSendMessages = chatPermissions.canSend && !isReadOnly;
  const isViewOnlyMode = isReadOnly || chatPermissions.isViewOnly;
  const canModerate = userRole === "creator" || userRole === "moderator";

  const loadMessages = async (before = null) => {
    try {
      if (before) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const options = {
        limit: 50,
        ...(before && { before }),
      };

      const response = await communityService.getMessages(communityId, options);
      const newMessages = response.data.messages || [];

      if (before) {
        setMessages((prev) => [...newMessages, ...prev]);
      } else {
        setMessages(newMessages);
        setTimeout(scrollToBottom, 100);
      }

      setHasMore(response.data.pagination?.hasNext || false);
    } catch (error) {
toast.error("Failed to load messages");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreMessages = () => {
    if (!hasMore || loadingMore || messages.length === 0) return;
    const oldestMessage = messages[0];
    if (oldestMessage) {
      loadMessages(oldestMessage._id);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    if (!rateLimiter.isAllowed("send_message", 15, 60000)) {
      toast.error("Too many messages sent. Please slow down.");
      logSecurityEvent("message_rate_limit_exceeded", {
        communityId,
        userId: user?.id,
      });
      return;
    }

    const rawContent = newMessage.trim();
    const contentError = validateMessageContent(rawContent);
    if (contentError) {
      toast.error(contentError);
      return;
    }

    const messageContent = sanitizeInput(rawContent);
    let messageType = "text";

    // Check for announcement command
    if (canModerate && messageContent.startsWith("/announce ")) {
      messageType = "announcement";
      const announcementContent = sanitizeInput(
        messageContent.replace("/announce ", "").trim()
      );
      if (!announcementContent) {
        toast.error("Announcement content cannot be empty");
        return;
      }

      setNewMessage("");
      setSending(true);

      try {
        communitySocketService.stopTyping(communityId);
        const response = await communitySocketService.sendMessage(
          communityId,
          announcementContent,
          "announcement"
        );

        if (response && response.data) {
          const optimisticMessage = {
            _id: response.data._id || `temp-${Date.now()}`,
            content: announcementContent,
            type: "announcement",
            sender: {
              _id: user.id || user._id,
              name: user.name || user.username,
              profilePhotoUrl: user.profilePic || user.avatar,
            },
            createdAt: new Date().toISOString(),
            community: communityId,
          };
          setMessages((prev) => [...prev, optimisticMessage]);
          setTimeout(scrollToBottom, 100);
        }
        toast.success("Announcement sent!");
      } catch (error) {
        toast.error("Failed to send announcement");
        setNewMessage(messageContent);
      } finally {
        setSending(false);
      }
      return;
    }

    setNewMessage("");
    setSending(true);

    try {
      if (socketConnected) {
        communitySocketService.stopTyping(communityId);
        const response = await communitySocketService.sendMessage(
          communityId,
          messageContent,
          messageType
        );

        if (response && response.data) {
          const optimisticMessage = {
            _id: response.data._id || `temp-${Date.now()}`,
            content: messageContent,
            type: messageType,
            sender: {
              _id: user.id || user._id,
              name: user.name || user.username,
              profilePhotoUrl: user.profilePic || user.avatar,
            },
            createdAt: new Date().toISOString(),
            community: communityId,
          };
          setMessages((prev) => [...prev, optimisticMessage]);
          setTimeout(scrollToBottom, 100);
        }
      } else {
        const response = await communityService.sendMessage(communityId, {
          content: messageContent,
          type: messageType,
        });

        if (response.data.message) {
          setMessages((prev) => [...prev, response.data.message]);
          setTimeout(scrollToBottom, 100);
        }
      }
    } catch (error) {
      // Handle restriction-specific errors with clear messages
      const errorMessage = error.response?.data?.message || error.message || "";
      if (errorMessage.toLowerCase().includes("banned")) {
        toast.error("🚫 You are banned from this community and cannot send messages.", { autoClose: 5000 });
      } else if (errorMessage.toLowerCase().includes("muted")) {
        toast.error("🔇 You are muted and cannot send messages.", { autoClose: 5000 });
      } else {
        const secureErrorMessage = handleSecureError(error);
        toast.error(secureErrorMessage);
      }
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const handleEditMessage = async (messageId, newContent) => {
    const contentError = validateMessageContent(newContent);
    if (contentError) {
      toast.error(contentError);
      return;
    }

    const sanitizedContent = sanitizeInput(newContent);

    if (!rateLimiter.isAllowed("edit_message", 10, 300000)) {
      toast.error("Too many message edits. Please slow down.");
      return;
    }

    const originalMessages = messages;
    setMessages((prev) =>
      prev.map((msg) =>
        msg._id === messageId ? { ...msg, content: sanitizedContent } : msg
      )
    );

    setEditingMessage(null);
    setEditContent("");

    try {
      if (socketConnected) {
        await communitySocketService.updateMessage(
          communityId,
          messageId,
          sanitizedContent
        );
      } else {
        await communityService.updateMessage(
          communityId,
          messageId,
          sanitizedContent
        );
      }
      toast.success("Message updated");
    } catch (error) {
      const secureErrorMessage = handleSecureError(error);
      toast.error(secureErrorMessage);
      setMessages(originalMessages);
      setEditingMessage(messageId);
      setEditContent(newContent);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?"))
      return;

    const originalMessages = messages;
    setMessages((prev) => prev.filter((msg) => msg._id !== messageId));

    try {
      if (socketConnected) {
        await communitySocketService.deleteMessage(communityId, messageId);
      } else {
        await communityService.deleteMessage(communityId, messageId);
      }
      toast.success("Message deleted");
    } catch (error) {
      toast.error(error.message || "Failed to delete message");
      setMessages(originalMessages);
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await communityService.searchMessages(
        communityId,
        query
      );
      setSearchResults(response.data.messages || []);
    } catch (error) {
      toast.error("Failed to search messages");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleTyping = () => {
    communitySocketService.startTyping(communityId);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      communitySocketService.stopTyping(communityId);
    }, 2000);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(query);
    }, 300);
  };

  // Socket event handlers
  const handleNewMessage = useCallback(
    (message) => {
      setMessages((prev) => {
        const isDuplicate = prev.some((msg) => msg._id === message._id);
        if (isDuplicate) return prev;
        return [...prev, message];
      });
      setTimeout(scrollToBottom, 100);
    },
    [scrollToBottom]
  );

  const handleMessageUpdated = useCallback((data) => {
    const messageId = data.messageId || data._id;
    const updatedContent = data.content;
    const updatedMessage = data.message || data;

    if (messageId) {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg._id === messageId) {
            if (updatedMessage && updatedMessage._id) {
              return updatedMessage;
            }
            return {
              ...msg,
              content: updatedContent || msg.content,
              updatedAt: data.updatedAt || new Date().toISOString(),
            };
          }
          return msg;
        })
      );
    }
  }, []);

  const handleMessageDeleted = useCallback((data) => {
    setMessages((prev) => prev.filter((msg) => msg._id !== data.messageId));
  }, []);

  const handleUserJoined = useCallback((data) => {
    if (data.user) {
      setOnlineMembers((prev) => {
        const alreadyExists = prev.find((m) => m._id === data.user._id);
        if (!alreadyExists) {
          return [...prev, data.user];
        }
        return prev;
      });
      toast.info(`${data.user.name} joined the chat`);
    }
  }, []);

  const handleUserLeft = useCallback((data) => {
    if (data.userId) {
      setOnlineMembers((prev) => prev.filter((m) => m._id !== data.userId));
    }
  }, []);

  const handleTypingStart = useCallback(
    (data) => {
      const userId = data.userId || data.user?._id;
      const userName = data.userName || data.user?.name;

      if (userId && userId !== user?.id && userId !== user?._id) {
        setTypingUsers((prev) => {
          const newMap = new Map(prev);
          newMap.set(userId, userName);
          return newMap;
        });
      }
    },
    [user?.id, user?._id]
  );

  const handleTypingStop = useCallback((data) => {
    const userId = data.userId || data.user?._id;
    if (userId) {
      setTypingUsers((prev) => {
        const newMap = new Map(prev);
        newMap.delete(userId);
        return newMap;
      });
    }
  }, []);

  const renderTypingIndicator = () => {
    if (typingUsers.size === 0) return null;

    const typingUserNames = Array.from(typingUsers.values()).slice(0, 3);
    const text =
      typingUserNames.length === 1
        ? `${typingUserNames[0]} is typing...`
        : typingUserNames.length === 2
        ? `${typingUserNames[0]} and ${typingUserNames[1]} are typing...`
        : `${typingUserNames[0]}, ${typingUserNames[1]} and others are typing...`;

    return (
      <motion.div
        className="collab-comm-chat-typing"
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 5 }}
      >
        <div className="collab-comm-chat-typing-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <span className="collab-comm-chat-typing-text">{text}</span>
      </motion.div>
    );
  };

  const renderMessage = (message) => {
    const isOwn = message.sender._id === user?.id;
    const canEdit = isOwn && message.type === "text";
    const canDelete = isOwn || canModerate;
    const isEditing = editingMessage === message._id;
    const isAnnouncement = message.type === "announcement";

    return (
      <motion.div
        key={message._id}
        className={`collab-comm-chat-message ${isOwn ? "own" : ""} ${
          isAnnouncement ? "announcement" : ""
        }`}
        variants={messageVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        layout
      >
        {!isOwn && (
          <div className="collab-comm-chat-message-avatar">
            <img
              src={message.sender.profilePhotoUrl || "/default-avatar.png"}
              alt={message.sender.name}
              onError={(e) => {
                e.target.src = "/default-avatar.png";
              }}
            />
          </div>
        )}

        <div className="collab-comm-chat-message-content">
          {!isOwn && (
            <div className="collab-comm-chat-message-header">
              <span className="collab-comm-chat-message-sender">
                {message.sender.name}
              </span>
              {isAnnouncement && (
                <span className="collab-comm-chat-announcement-badge">
                  <FiVolume2 size={12} />
                  Announcement
                </span>
              )}
            </div>
          )}

          <div className="collab-comm-chat-message-body">
            {isEditing ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleEditMessage(message._id, editContent);
                }}
                className="collab-comm-chat-edit-form"
              >
                <input
                  type="text"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setEditingMessage(null);
                      setEditContent("");
                    }
                  }}
                  autoFocus
                  className="collab-comm-chat-edit-input"
                />
                <div className="collab-comm-chat-edit-actions">
                  <button type="submit" className="collab-comm-chat-edit-save">
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingMessage(null);
                      setEditContent("");
                    }}
                    className="collab-comm-chat-edit-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <p className="collab-comm-chat-message-text">{message.content}</p>
            )}
          </div>

          <div className="collab-comm-chat-message-footer">
            <span className="collab-comm-chat-message-time">
              {formatDistanceToNow(new Date(message.createdAt))}
            </span>

            {(canEdit || canDelete) && !isEditing && (
              <div className="collab-comm-chat-message-actions">
                <button
                  className="collab-comm-chat-action-trigger"
                  onClick={() =>
                    setActiveDropdown(
                      activeDropdown === message._id ? null : message._id
                    )
                  }
                >
                  <FiMoreVertical size={14} />
                </button>

                <AnimatePresence>
                  {activeDropdown === message._id && (
                    <motion.div
                      className="collab-comm-chat-actions-dropdown"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                    >
                      {canEdit && (
                        <button
                          onClick={() => {
                            setEditingMessage(message._id);
                            setEditContent(message.content);
                            setActiveDropdown(null);
                          }}
                          className="collab-comm-chat-action-item"
                        >
                          <FiEdit2 size={14} />
                          Edit
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => {
                            handleDeleteMessage(message._id);
                            setActiveDropdown(null);
                          }}
                          className="collab-comm-chat-action-item delete"
                        >
                          <FiTrash2 size={14} />
                          Delete
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="collab-comm-chat-container">
        <div className="collab-comm-chat-loading">
          <div className="collab-comm-loading-spinner"></div>
          <p>Loading chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="collab-comm-chat-container">
      {/* Header */}
      <div className="collab-comm-chat-header">
        <div className="collab-comm-chat-header-info">
          <FiMessageCircle size={20} />
          <span className="collab-comm-chat-title">Community Chat</span>
          <span className="collab-comm-chat-online">
            <FiUsers size={14} />
            {onlineMembers.length} online
          </span>
          {!socketConnected && (
            <span className="collab-comm-chat-status-offline">
              <FiAlertCircle size={14} />
              HTTP mode
            </span>
          )}
        </div>

        <div className="collab-comm-chat-header-actions">
          <motion.button
            onClick={() => setShowSearch(!showSearch)}
            className={`collab-comm-chat-header-btn ${
              showSearch ? "active" : ""
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {showSearch ? <FiX size={16} /> : <FiSearch size={16} />}
          </motion.button>
          <motion.button
            onClick={() => setNotifications(!notifications)}
            className={`collab-comm-chat-header-btn ${
              notifications ? "active" : ""
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {notifications ? <FiVolume2 size={16} /> : <FiVolumeX size={16} />}
          </motion.button>
        </div>
      </div>

      {/* Search */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            className="collab-comm-chat-search"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <div className="collab-comm-chat-search-input-wrapper">
              <FiSearch size={16} />
              <input
                type="text"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="collab-comm-chat-search-input"
              />
              {isSearching && (
                <div className="collab-comm-chat-search-loading">
                  Searching...
                </div>
              )}
            </div>

            {searchResults.length > 0 && (
              <div className="collab-comm-chat-search-results">
                {searchResults.map((message) => (
                  <div
                    key={message._id}
                    className="collab-comm-chat-search-result"
                  >
                    <strong>{message.sender.name}</strong>: {message.content}
                    <span className="collab-comm-chat-search-result-time">
                      {formatDistanceToNow(new Date(message.createdAt))}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages */}
      <div className="collab-comm-chat-messages" ref={messageContainerRef}>
        {hasMore && (
          <div className="collab-comm-chat-load-more">
            <motion.button
              onClick={loadMoreMessages}
              disabled={loadingMore}
              className="collab-comm-chat-load-more-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loadingMore ? "Loading..." : "Load older messages"}
            </motion.button>
          </div>
        )}

        {messages.length === 0 ? (
          <motion.div
            className="collab-comm-chat-empty"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FiMessageCircle size={48} />
            <h3>Start the conversation</h3>
            <p>Be the first to send a message in this community!</p>
          </motion.div>
        ) : (
          <div className="collab-comm-chat-messages-list">
            <AnimatePresence mode="popLayout">
              {messages.map(renderMessage)}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Typing Indicator */}
      <AnimatePresence>{renderTypingIndicator()}</AnimatePresence>

      {/* Input */}
      <div className="collab-comm-chat-input-container">
        {canSendMessages ? (
          <>
            <form
              onSubmit={handleSendMessage}
              className="collab-comm-chat-input-form"
            >
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  handleTyping();
                }}
                placeholder={
                  canModerate
                    ? "Type a message... (/announce for announcements)"
                    : "Type a message..."
                }
                disabled={sending}
                className="collab-comm-chat-input"
                maxLength={1000}
              />
              <motion.button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="collab-comm-chat-send-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {sending ? (
                  <div className="collab-comm-btn-spinner"></div>
                ) : (
                  <FiSend size={16} />
                )}
              </motion.button>
            </form>

            {canModerate && (
              <div className="collab-comm-chat-hint">
                <FiAlertCircle size={12} />
                As a moderator, use "/announce" to send announcements
              </div>
            )}
          </>
        ) : (
          <div className="collab-comm-chat-readonly">
            <FiAlertCircle size={16} />
            <span>
              {isViewOnlyMode
                ? "You're viewing in read-only mode. Join the community to chat!"
                : "You don't have permission to send messages."}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityChat;
